// src/utils/enhancedPDFGenerator.js

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// ============================================================
// COMPLETE ENHANCED PDF REPORT GENERATOR - FULLY FIXED
// ============================================================

export async function generateEnhancedPDFReport(solutionData, elements) {
  const pdf = new jsPDF('landscape', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth(); // 297mm
  const pageHeight = pdf.internal.pageSize.getHeight(); // 210mm
  const margin = 10;
  const contentWidth = pageWidth - margin * 2; // 277mm

  try {
    // ============================================================
    // 1. COVER PAGE
    // ============================================================
    generateCoverPage(pdf, solutionData, pageWidth, pageHeight);

    // ============================================================
    // 2. TABLE OF CONTENTS
    // ============================================================
    pdf.addPage();
    generateTOC(pdf, margin);

    // ============================================================
    // 3. USER SPECIFIED DETAILS (Industry, Problems, Problem Types, Data Types, Technical Details)
    // ============================================================
    pdf.addPage();
    generateUserSpecifiedDetails(pdf, solutionData, margin, contentWidth, pageHeight);

    // ============================================================
    // 4. EXECUTIVE SUMMARY
    // ============================================================
    pdf.addPage();
    generateExecutiveSummary(pdf, solutionData, margin, contentWidth, pageHeight);

    // ============================================================
    // 5. REQUIREMENTS WITH APPROACHES
    // ============================================================
    pdf.addPage();
    generateRequirementsWithApproaches(pdf, solutionData, margin, contentWidth, pageHeight);

    // ============================================================
    // 6. APPROACH TRACEABILITY (To Component Level)
    // ============================================================
    pdf.addPage();
    generateApproachToComponentTraceability(pdf, solutionData, margin, contentWidth, pageHeight);

    // ============================================================
    // 7. ALLUVIAL DIAGRAM
    // ============================================================
    if (elements?.alluvialElement) {
      pdf.addPage();
      await generateImagePage(pdf, elements.alluvialElement, margin, contentWidth, pageHeight, 'Alluvial Traceability Diagram', 'Figure 1: Full Traceability Flow');
    }

    // ============================================================
    // 8. ARCHITECTURE DIAGRAM
    // ============================================================
    if (elements?.architectureElement) {
      pdf.addPage();
      await generateImagePage(pdf, elements.architectureElement, margin, contentWidth, pageHeight, 'Solution Architecture Diagram', 'Figure 2: Layered Architecture');
    }

    // ============================================================
    // 9. MULTI-LEVEL EXPANDED STRUCTURE
    // ============================================================
    pdf.addPage();
    generateMultiLevelStructure(pdf, solutionData, margin, contentWidth, pageHeight);

    // ============================================================
    // 10. BILL OF MATERIALS (FIXED COLUMN WIDTHS)
    // ============================================================
    pdf.addPage();
    generateBoM(pdf, solutionData, margin, contentWidth, pageHeight);

    // ============================================================
    // 11. COMPONENT SPECIFICATIONS
    // ============================================================
    pdf.addPage();
    generateComponentSpecifications(pdf, solutionData, margin, contentWidth, pageHeight);

    // ============================================================
    // 12. COST ANALYSIS
    // ============================================================
    pdf.addPage();
    generateCostAnalysis(pdf, solutionData, margin, contentWidth, pageHeight);

    // ============================================================
    // 13. VALIDATION REPORT
    // ============================================================
    pdf.addPage();
    generateValidationReport(pdf, solutionData, margin, contentWidth, pageHeight);

    // ============================================================
    // 14. IMPLEMENTATION TIMELINE
    // ============================================================
    pdf.addPage();
    generateTimeline(pdf, margin, contentWidth, pageHeight);

    // ============================================================
    // 15. RISK ASSESSMENT
    // ============================================================
    pdf.addPage();
    generateRiskAssessment(pdf, margin, contentWidth, pageHeight);

    // ============================================================
    // 16. EXPERT RECOMMENDATIONS
    // ============================================================
    pdf.addPage();
    generateRecommendations(pdf, margin, contentWidth, pageHeight);

    // ============================================================
    // ADD FOOTERS
    // ============================================================
    addFooters(pdf, solutionData, pageWidth, pageHeight);

    // Save PDF
    const filename = `${(solutionData?.name || 'ai-solution').replace(/\s+/g, '-').toLowerCase()}-complete-report.pdf`;
    pdf.save(filename);
    return pdf;
    
  } catch (error) {
    console.error('PDF generation error:', error);
    throw error;
  }
}

// ============================================================
// 1. COVER PAGE
// ============================================================
function generateCoverPage(pdf, data, pageWidth, pageHeight) {
  pdf.setFillColor(30, 64, 175);
  pdf.rect(0, 0, pageWidth, pageHeight, 'F');
  
  pdf.setFillColor(59, 130, 246);
  pdf.circle(pageWidth - 25, 25, 40, 'F');
  pdf.setFillColor(37, 99, 235);
  pdf.circle(pageWidth - 60, 70, 25, 'F');
  
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(32);
  pdf.setFont('helvetica', 'bold');
  pdf.text('AI Solution Architecture', pageWidth / 2, 90, { align: 'center' });
  pdf.text('Complete Report', pageWidth / 2, 108, { align: 'center' });
  
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'normal');
  pdf.text(data?.name || 'Untitled Solution', pageWidth / 2, 130, { align: 'center' });
  
  pdf.setFontSize(10);
  pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 145, { align: 'center' });
  
  const stats = [
    { label: 'Components', value: String(data?.components?.length || 0) },
    { label: 'Layers', value: String(new Set(data?.components?.map(c => c.layerId) || []).size) },
    { label: 'Approaches', value: String(new Set(data?.requirements?.map(r => r.approachId).filter(Boolean) || []).size) },
    { label: 'Est. Cost', value: `$${(data?.totalCost?.high || 0).toFixed(0)}` }
  ];
  
  const boxWidth = 50;
  const boxGap = 8;
  const totalBoxWidth = stats.length * boxWidth + (stats.length - 1) * boxGap;
  const startX = (pageWidth - totalBoxWidth) / 2;
  
  stats.forEach((stat, i) => {
    const x = startX + i * (boxWidth + boxGap);
    pdf.setFillColor(255, 255, 255);
    pdf.roundedRect(x, 160, boxWidth, 25, 5, 5, 'F');
    pdf.setTextColor(30, 64, 175);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text(stat.value, x + boxWidth / 2, 172, { align: 'center' });
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.text(stat.label, x + boxWidth / 2, 181, { align: 'center' });
  });
}

// ============================================================
// 2. TABLE OF CONTENTS
// ============================================================
function generateTOC(pdf, margin) {
  pdf.setFontSize(22);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('Table of Contents', margin, 25);
  pdf.line(margin, 30, pdf.internal.pageSize.getWidth() - margin, 30);
  
  const sections = [
    '1. User Specified Details',
    '2. Executive Summary',
    '3. Requirements with Approaches',
    '4. Approach to Component Traceability',
    '5. Alluvial Traceability Diagram',
    '6. Solution Architecture Diagram',
    '7. Multi-Level Expanded Structure',
    '8. Bill of Materials (BoM)',
    '9. Component Specifications',
    '10. Cost Analysis',
    '11. Validation Report',
    '12. Implementation Timeline',
    '13. Risk Assessment',
    '14. Expert Recommendations'
  ];
  
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(0, 0, 0);
  
  let y = 45;
  sections.forEach((section) => {
    pdf.text(section, margin + 5, y);
    y += 10;
  });
}


function generateSolutionMeta(pdf, data, margin, contentWidth, pageHeight) {
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('Solution Metadata', margin, 25);
  
  pdf.setDrawColor(30, 64, 175);
  pdf.line(margin, 30, margin + contentWidth, 30);
  
  let y = 42;
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(0, 0, 0);
  
  const meta = data.solutionMeta || {};
  const lines = [
    `Version: ${meta.version || '1.0.0'}`,
    `Author: ${meta.author || 'N/A'}`,
    `Stakeholders: ${(meta.stakeholders || []).join(', ') || 'N/A'}`,
    `Solution Type: ${meta.solutionType || 'N/A'}`,
    `Deployment Type: ${meta.deploymentType || 'N/A'}`,
    `Environment: ${meta.environmentType || 'N/A'}`,
    `Reusability: ${meta.reusability || 'N/A'}`,
    `Audience: ${meta.audience || 'N/A'}`
  ];
  
  lines.forEach(line => {
    pdf.text(line, margin, y);
    y += 7;
  });
}

// ============================================================
// 3. USER SPECIFIED DETAILS - NEW SECTION
// ============================================================
function generateUserSpecifiedDetails(pdf, data, margin, contentWidth, pageHeight) {
  pdf.setFontSize(22);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('User Specified Details', margin, 25);
  pdf.line(margin, 30, margin + contentWidth, 30);
  
  let y = 42;
  const req = data?.autoArchitectRequirements || {};
  
  // ==================== INDUSTRY ====================
  pdf.setFillColor(30, 64, 175);
  pdf.rect(margin, y - 3, contentWidth, 8, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Industry', margin + 5, y + 3);
  y += 12;
  
  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Selected Industry: ${req.industry || 'Not Specified'}`, margin + 10, y);
  y += 10;
  
  // ==================== PROBLEMS ====================
  pdf.setFillColor(30, 64, 175);
  pdf.rect(margin, y - 3, contentWidth, 8, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Selected Problems', margin + 5, y + 3);
  y += 12;
  
  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  const problems = req.problems || [];
  if (problems.length > 0) {
    problems.forEach((pId) => {
      pdf.text(`• ${pId}`, margin + 10, y);
      y += 6;
    });
  } else {
    pdf.text('No specific problems selected', margin + 10, y);
    y += 6;
  }
  y += 5;
  
  // ==================== PROBLEM TYPES ====================
  pdf.setFillColor(30, 64, 175);
  pdf.rect(margin, y - 3, contentWidth, 8, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Problem Types', margin + 5, y + 3);
  y += 12;
  
  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  const problemTypes = req.problemTypes || [];
  if (problemTypes.length > 0) {
    // Show as comma-separated on wrapped lines
    const problemTypesText = problemTypes.join(', ');
    const lines = pdf.splitTextToSize(problemTypesText, contentWidth - 20);
    pdf.text(lines, margin + 10, y);
    y += lines.length * 6;
  } else {
    pdf.text('No specific problem types selected', margin + 10, y);
    y += 6;
  }
  y += 5;
  
  // ==================== DATA TYPES ====================
  pdf.setFillColor(30, 64, 175);
  pdf.rect(margin, y - 3, contentWidth, 8, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Data Types', margin + 5, y + 3);
  y += 12;
  
  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  const dataTypes = req.dataTypes || [];
  if (dataTypes.length > 0) {
    const dataTypesText = dataTypes.join(', ');
    const lines = pdf.splitTextToSize(dataTypesText, contentWidth - 20);
    pdf.text(lines, margin + 10, y);
    y += lines.length * 6;
  } else {
    pdf.text('No specific data types selected', margin + 10, y);
    y += 6;
  }
  y += 5;
  
  // ==================== TECHNICAL DETAILS ====================
  pdf.setFillColor(30, 64, 175);
  pdf.rect(margin, y - 3, contentWidth, 8, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Technical Details', margin + 5, y + 3);
  y += 12;
  
  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  
  const techDetails = [
    ['Scale', req.scale || 'Not Specified'],
    ['Budget', req.budget || 'Not Specified'],
    ['Latency', req.latency || 'Not Specified'],
    ['Deployment', req.deployment || 'Not Specified'],
    ['Compliance', req.compliance || 'Not Specified'],
    ['Security Level', req.securityLevel || 'Not Specified'],
    ['Expertise', req.expertise || 'Not Specified'],
    ['Priority', req.priority || 'Not Specified'],
    ['Data Volume', req.dataVolume || 'Not Specified'],
    ['Timeframe', req.timeframe || 'Not Specified']
  ];
  
  techDetails.forEach(([label, value]) => {
    if (y > pageHeight - 15) {
      pdf.addPage();
      y = 25;
    }
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${label}:`, margin + 10, y);
    pdf.setFont('helvetica', 'normal');
    pdf.text(value, margin + 60, y);
    y += 7;
  });
}

// ============================================================
// 4. EXECUTIVE SUMMARY
// ============================================================
function generateExecutiveSummary(pdf, data, margin, contentWidth, pageHeight) {
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('Executive Summary', margin, 25);
  pdf.line(margin, 30, margin + contentWidth, 30);
  
  let y = 42;
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(0, 0, 0);
  
  const lines = [
    `Solution Name: ${data?.name || 'Untitled'}`,
    `Total Components: ${data?.components?.length || 0}`,
    `Layers Covered: ${new Set(data?.components?.map(c => c.layerId) || []).size} / 12`,
    `Approaches Used: ${new Set(data?.requirements?.map(r => r.approachId).filter(Boolean) || []).size} / 40`,
    `Estimated Cost: $${(data?.totalCost?.low || 0).toFixed(2)} - $${(data?.totalCost?.high || 0).toFixed(2)}`,
    `Validation Status: ${data?.validation?.isValid ? 'PASSED' : 'HAS ISSUES'}`
  ];
  
  lines.forEach(line => {
    pdf.text(line, margin, y);
    y += 7;
  });
}

// ============================================================
// 5. REQUIREMENTS WITH APPROACHES
// ============================================================
function generateRequirementsWithApproaches(pdf, data, margin, contentWidth, pageHeight) {
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('Requirements with Approaches', margin, 25);
  pdf.line(margin, 30, margin + contentWidth, 30);
  
  let y = 42;
  const requirements = data?.requirements || [];
  
  // Table Header
  pdf.setFillColor(30, 64, 175);
  pdf.rect(margin, y, contentWidth, 10, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Requirement', margin + 5, y + 7);
  pdf.text('Category', margin + 80, y + 7);
  pdf.text('Priority', margin + 130, y + 7);
  pdf.text('Approach', margin + 160, y + 7);
  pdf.text('Status', margin + 240, y + 7);
  y += 12;
  
  requirements.forEach((req, i) => {
    if (y > pageHeight - 15) {
      pdf.addPage();
      y = 25;
    }
    
    if (i % 2 === 0) {
      pdf.setFillColor(245, 247, 250);
      pdf.rect(margin, y, contentWidth, 7, 'F');
    }
    
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.text((req.name || 'Requirement').substring(0, 30), margin + 5, y + 5);
    pdf.text((req.category || 'General').substring(0, 15), margin + 80, y + 5);
    pdf.text(req.priority || 'High', margin + 130, y + 5);
    pdf.text((req.approachName || 'Default').substring(0, 20), margin + 160, y + 5);
    pdf.text(req.status || 'satisfied', margin + 240, y + 5);
    y += 7;
  });
}

// ============================================================
// 6. APPROACH TO COMPONENT TRACEABILITY (FIXED - Goes to Component Level)
// ============================================================
function generateApproachToComponentTraceability(pdf, data, margin, contentWidth, pageHeight) {
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('Approach to Component Traceability', margin, 25);
  pdf.line(margin, 30, margin + contentWidth, 30);
  
  let y = 42;
  const components = data?.components || [];
  const requirements = data?.requirements || [];
  
  // Group components by approach
  const approachGroups = {};
  
  requirements.forEach(req => {
    if (!req.approachId) return;
    const key = req.approachId;
    if (!approachGroups[key]) {
      approachGroups[key] = {
        approachId: key,
        approachName: req.approachName || `Approach ${key}`,
        requirements: [],
        components: []
      };
    }
    approachGroups[key].requirements.push(req.name);
  });
  
  // Map components to approaches based on approachId or layer matching
  components.forEach(comp => {
    const compApproachId = comp.approachId;
    if (compApproachId && approachGroups[compApproachId]) {
      approachGroups[compApproachId].components.push(comp);
    } else {
      // Try to match by layer
      Object.values(approachGroups).forEach(group => {
        const mappedLayers = getLayersForApproach(group.approachId);
        if (mappedLayers.includes(comp.layerId)) {
          group.components.push(comp);
        }
      });
    }
  });
  
  // Render each approach group
  Object.values(approachGroups).forEach((group) => {
    if (y > pageHeight - 25) {
      pdf.addPage();
      y = 25;
    }
    
    // Approach header
    pdf.setFillColor(240, 253, 244);
    pdf.rect(margin, y - 4, contentWidth, 9, 'F');
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 64, 175);
    pdf.text(`${group.approachName} (${group.components.length} components)`, margin + 5, y + 3);
    y += 10;
    
    // Requirements under this approach
    if (group.requirements.length > 0) {
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      const reqText = group.requirements.join(', ');
      pdf.text(`Requirements: ${reqText.substring(0, 100)}`, margin + 8, y);
      y += 6;
    }
    
    // Components under this approach
    group.components.forEach(comp => {
      if (y > pageHeight - 12) {
        pdf.addPage();
        y = 25;
      }
      
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(0, 0, 0);
      pdf.text(`  • ${comp.name}`, margin + 8, y);
      y += 5;
    });
    y += 4;
  });
}

// ============================================================
// 7. IMAGE PAGE
// ============================================================
async function generateImagePage(pdf, element, margin, contentWidth, pageHeight, title, caption) {
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text(title, margin, 25);
  pdf.line(margin, 30, margin + contentWidth, 30);
  
  try {
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      width: element.scrollWidth || element.offsetWidth,
      height: element.scrollHeight || element.offsetHeight
    });
    
    const imgData = canvas.toDataURL('image/png');
    const imgWidth = contentWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const maxHeight = pageHeight - 45;
    
    pdf.addImage(imgData, 'PNG', margin, 35, imgWidth, Math.min(imgHeight, maxHeight));
    
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'italic');
    pdf.setTextColor(100, 100, 100);
    pdf.text(caption, margin, 35 + Math.min(imgHeight, maxHeight) + 4);
  } catch (error) {
    console.error('Image capture error:', error);
    pdf.setFontSize(10);
    pdf.setTextColor(255, 0, 0);
    pdf.text('Image could not be rendered: ' + error.message, margin, 50);
  }
}

// ============================================================
// 8. MULTI-LEVEL EXPANDED STRUCTURE
// ============================================================
function generateMultiLevelStructure(pdf, data, margin, contentWidth, pageHeight) {
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('Multi-Level Expanded Structure', margin, 25);
  pdf.line(margin, 30, margin + contentWidth, 30);
  
  let y = 42;
  const components = data?.components || [];
  
  const layerGroups = {};
  components.forEach(comp => {
    const layerKey = comp.layerId || 'unknown';
    const layerName = comp.layerName || 'Layer';
    if (!layerGroups[layerKey]) {
      layerGroups[layerKey] = { layerName, subLayers: {} };
    }
    const subLayerKey = comp.subLayerId || 'unknown';
    const subLayerName = comp.subLayerName || 'Sub-Layer';
    if (!layerGroups[layerKey].subLayers[subLayerKey]) {
      layerGroups[layerKey].subLayers[subLayerKey] = { subLayerName, components: [] };
    }
    layerGroups[layerKey].subLayers[subLayerKey].components.push(comp);
  });
  
  Object.entries(layerGroups).forEach(([layerId, layerData]) => {
    if (y > pageHeight - 20) {
      pdf.addPage();
      y = 25;
    }
    
    pdf.setFillColor(30, 64, 175);
    pdf.rect(margin, y - 3, contentWidth, 7, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`Layer: ${layerData.layerName}`, margin + 5, y + 2);
    y += 8;
    
    Object.entries(layerData.subLayers).forEach(([subLayerId, subLayerData]) => {
      if (y > pageHeight - 15) {
        pdf.addPage();
        y = 25;
      }
      
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`Sub-Layer: ${subLayerData.subLayerName}`, margin + 8, y);
      y += 6;
      
      subLayerData.components.forEach(comp => {
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`  • ${comp.name} (Qty: ${comp.quantity || 1})`, margin + 12, y);
        y += 5;
      });
      y += 2;
    });
    y += 4;
  });
}

// ============================================================
// 9. BILL OF MATERIALS - FIXED COLUMN WIDTHS
// ============================================================
function generateBoM(pdf, data, margin, contentWidth, pageHeight) {
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('Bill of Materials (BoM)', margin, 25);
  pdf.line(margin, 30, margin + contentWidth, 30);
  
  let y = 42;
  const components = data?.components || [];
  
  // FIXED COLUMN WIDTHS (Total = contentWidth = 277mm)
  // #: 8mm | Component: 70mm | Layer: 45mm | Sub-Layer: 50mm | Qty: 15mm | Unit Cost: 40mm | Total: 49mm = 277mm
  const colWidths = {
    num: 8,
    name: 70,
    layer: 45,
    subLayer: 50,
    qty: 15,
    unitCost: 40,
    total: 49
  };
  
  const colStarts = {
    num: margin,
    name: margin + 8,
    layer: margin + 8 + 70,
    subLayer: margin + 8 + 70 + 45,
    qty: margin + 8 + 70 + 45 + 50,
    unitCost: margin + 8 + 70 + 45 + 50 + 15,
    total: margin + 8 + 70 + 45 + 50 + 15 + 40
  };
  
  // Table Header
  pdf.setFillColor(30, 64, 175);
  pdf.rect(margin, y, contentWidth, 9, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'bold');
  pdf.text('#', colStarts.num + 2, y + 6);
  pdf.text('Component', colStarts.name + 2, y + 6);
  pdf.text('Layer', colStarts.layer + 2, y + 6);
  pdf.text('Sub-Layer', colStarts.subLayer + 2, y + 6);
  pdf.text('Qty', colStarts.qty + 2, y + 6);
  pdf.text('Unit Cost', colStarts.unitCost + 2, y + 6);
  pdf.text('Total Cost', colStarts.total + 2, y + 6);
  y += 10;
  
  // Column separator lines
  [colStarts.name, colStarts.layer, colStarts.subLayer, colStarts.qty, colStarts.unitCost, colStarts.total].forEach(x => {
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.3);
    pdf.line(x, y - 10, x, pageHeight - 15);
  });
  
  // Table Rows
  components.forEach((comp, i) => {
    if (y > pageHeight - 15) {
      pdf.addPage();
      y = 25;
      // Redraw header on new page
      pdf.setFillColor(30, 64, 175);
      pdf.rect(margin, y, contentWidth, 9, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'bold');
      pdf.text('#', colStarts.num + 2, y + 6);
      pdf.text('Component', colStarts.name + 2, y + 6);
      pdf.text('Layer', colStarts.layer + 2, y + 6);
      pdf.text('Sub-Layer', colStarts.subLayer + 2, y + 6);
      pdf.text('Qty', colStarts.qty + 2, y + 6);
      pdf.text('Unit Cost', colStarts.unitCost + 2, y + 6);
      pdf.text('Total Cost', colStarts.total + 2, y + 6);
      y += 10;
    }
    
    if (i % 2 === 0) {
      pdf.setFillColor(245, 247, 250);
      pdf.rect(margin, y, contentWidth, 6, 'F');
    }
    
    const qty = comp.quantity || 1;
    const unitLow = comp.cost?.low || 0;
    const unitHigh = comp.cost?.high || 0;
    const totalLow = unitLow * qty;
    const totalHigh = unitHigh * qty;
    
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(6);
    pdf.setFont('helvetica', 'normal');
    pdf.text(String(i + 1), colStarts.num + 2, y + 5);
    pdf.text((comp.name || '').substring(0, 35), colStarts.name + 2, y + 5);
    pdf.text((comp.layerName || '').substring(0, 20), colStarts.layer + 2, y + 5);
    pdf.text((comp.subLayerName || '').substring(0, 22), colStarts.subLayer + 2, y + 5);
    pdf.text(String(qty), colStarts.qty + 2, y + 5);
    pdf.text(`$${unitLow.toFixed(0)}-${unitHigh.toFixed(0)}`, colStarts.unitCost + 2, y + 5);
    pdf.text(`$${totalLow.toFixed(0)}-${totalHigh.toFixed(0)}`, colStarts.total + 2, y + 5);
    y += 6;
  });
  
  // Total Row
  y += 3;
  pdf.setFillColor(30, 64, 175);
  pdf.rect(margin, y, contentWidth, 8, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.text('TOTAL', colStarts.name + 2, y + 6);
  pdf.text(`$${(data?.totalCost?.low || 0).toFixed(0)} - $${(data?.totalCost?.high || 0).toFixed(0)}`, colStarts.total + 2, y + 6);
}

// ============================================================
// 10. COMPONENT SPECIFICATIONS
// ============================================================
function generateComponentSpecifications(pdf, data, margin, contentWidth, pageHeight) {
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('Component Specifications', margin, 25);
  pdf.line(margin, 30, margin + contentWidth, 30);
  
  let y = 42;
  const components = data?.components || [];
  
  components.forEach((comp) => {
    const specs = comp.specifications || comp.sizingInfo?.specifications;
    if (!specs) return;
    
    if (y > pageHeight - 30) {
      pdf.addPage();
      y = 25;
    }
    
    pdf.setFillColor(240, 249, 255);
    pdf.rect(margin, y - 3, contentWidth, 7, 'F');
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 64, 175);
    pdf.text(`${comp.icon || ''} ${comp.name}`, margin + 5, y + 2);
    y += 9;
    
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0);
    
    Object.entries(specs).forEach(([key, value]) => {
      if (key === 'costRange' || value === null || value === undefined || typeof value === 'object') return;
      if (y > pageHeight - 10) {
        pdf.addPage();
        y = 25;
      }
      const label = key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim();
      pdf.text(`  ${label}: ${value}`, margin + 8, y);
      y += 4;
    });
    y += 3;
  });
}

// ============================================================
// 11. COST ANALYSIS
// ============================================================
function generateCostAnalysis(pdf, data, margin, contentWidth, pageHeight) {
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('Cost Analysis', margin, 25);
  pdf.line(margin, 30, margin + contentWidth, 30);
  
  let y = 45;
  const totalCost = data?.totalCost || { low: 0, high: 0 };
  
  const boxWidth = contentWidth / 3 - 5;
  const boxes = [
    { label: 'Low Cost', value: `$${totalCost.low.toFixed(0)}` },
    { label: 'High Cost', value: `$${totalCost.high.toFixed(0)}` },
    { label: 'Average', value: `$${((totalCost.low + totalCost.high) / 2).toFixed(0)}` }
  ];
  
  boxes.forEach((box, i) => {
    const x = margin + i * (boxWidth + 5);
    pdf.setFillColor(240, 249, 255);
    pdf.roundedRect(x, y, boxWidth, 20, 4, 4, 'F');
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 64, 175);
    pdf.text(box.value, x + boxWidth / 2, y + 10, { align: 'center' });
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.text(box.label, x + boxWidth / 2, y + 17, { align: 'center' });
  });
  
  // Cost breakdown table
  y += 35;
  const colWidths = { name: 120, qty: 20, unitLow: 35, unitHigh: 35, totalLow: 35, totalHigh: 32 };
  const colStarts = {
    name: margin,
    qty: margin + 120,
    unitLow: margin + 140,
    unitHigh: margin + 175,
    totalLow: margin + 210,
    totalHigh: margin + 245
  };
  
  // Header
  pdf.setFillColor(30, 64, 175);
  pdf.rect(margin, y, contentWidth, 8, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Component', colStarts.name + 2, y + 6);
  pdf.text('Qty', colStarts.qty + 2, y + 6);
  pdf.text('Unit Low', colStarts.unitLow + 2, y + 6);
  pdf.text('Unit High', colStarts.unitHigh + 2, y + 6);
  pdf.text('Total Low', colStarts.totalLow + 2, y + 6);
  pdf.text('Total High', colStarts.totalHigh + 2, y + 6);
  y += 9;
  
  totalCost.breakdown?.forEach((item, i) => {
    if (y > pageHeight - 12) {
      pdf.addPage();
      y = 25;
    }
    if (i % 2 === 0) {
      pdf.setFillColor(245, 247, 250);
      pdf.rect(margin, y, contentWidth, 6, 'F');
    }
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(6);
    pdf.setFont('helvetica', 'normal');
    pdf.text((item.component || '').substring(0, 50), colStarts.name + 2, y + 5);
    pdf.text(String(item.quantity || 1), colStarts.qty + 2, y + 5);
    pdf.text(`$${item.low?.toFixed?.(0) || 0}`, colStarts.unitLow + 2, y + 5);
    pdf.text(`$${item.high?.toFixed?.(0) || 0}`, colStarts.unitHigh + 2, y + 5);
    pdf.text(`$${((item.low || 0) * (item.quantity || 1)).toFixed(0)}`, colStarts.totalLow + 2, y + 5);
    pdf.text(`$${((item.high || 0) * (item.quantity || 1)).toFixed(0)}`, colStarts.totalHigh + 2, y + 5);
    y += 6;
  });
}

// ============================================================
// 12. VALIDATION REPORT
// ============================================================
function generateValidationReport(pdf, data, margin, contentWidth, pageHeight) {
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('Validation Report', margin, 25);
  pdf.line(margin, 30, margin + contentWidth, 30);
  
  let y = 42;
  const validation = data?.validation || { issues: [], warnings: [], isValid: true };
  
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  if (validation.isValid) {
    pdf.setTextColor(0, 128, 0);
    pdf.text('Solution is VALID', margin, y);
  } else {
    pdf.setTextColor(255, 0, 0);
    pdf.text('Solution has ISSUES', margin, y);
  }
  y += 10;
  
  if (validation.issues?.length > 0) {
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0);
    validation.issues.forEach(issue => {
      pdf.text(`• ${issue}`, margin + 5, y);
      y += 5;
    });
  }
}

// ============================================================
// 13. IMPLEMENTATION TIMELINE
// ============================================================
function generateTimeline(pdf, margin, contentWidth, pageHeight) {
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('Implementation Timeline', margin, 25);
  pdf.line(margin, 30, margin + contentWidth, 30);
  
  let y = 45;
  const phases = [
    { name: 'Phase 1: Foundation', duration: '2-4 weeks', focus: 'Infrastructure setup' },
    { name: 'Phase 2: Data Infrastructure', duration: '3-6 weeks', focus: 'Data pipelines and storage' },
    { name: 'Phase 3: Model Development', duration: '4-8 weeks', focus: 'Model training and tuning' },
    { name: 'Phase 4: Deployment', duration: '2-3 weeks', focus: 'Model serving and API' },
    { name: 'Phase 5: Application Integration', duration: '3-4 weeks', focus: 'Frontend and backend' },
    { name: 'Phase 6: Security & Compliance', duration: '2-3 weeks', focus: 'Security hardening' },
    { name: 'Phase 7: Monitoring & Governance', duration: '2-3 weeks', focus: 'Monitoring setup' }
  ];
  
  phases.forEach((phase, i) => {
    pdf.setFillColor(30, 64, 175);
    pdf.circle(margin + 8, y + 3, 4, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'bold');
    pdf.text(String(i + 1), margin + 8, y + 5, { align: 'center' });
    
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text(phase.name, margin + 18, y + 5);
    
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text(phase.duration + ' - ' + phase.focus, margin + 18, y + 11);
    
    y += 20;
  });
}

// ============================================================
// 14. RISK ASSESSMENT
// ============================================================
function generateRiskAssessment(pdf, margin, contentWidth, pageHeight) {
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('Risk Assessment', margin, 25);
  pdf.line(margin, 30, margin + contentWidth, 30);
  
  let y = 42;
  const risks = [
    { risk: 'Data quality issues', impact: 'High', probability: 'Medium', mitigation: 'Data validation' },
    { risk: 'Model performance degradation', impact: 'High', probability: 'Medium', mitigation: 'Monitoring' },
    { risk: 'Infrastructure cost overrun', impact: 'Medium', probability: 'Medium', mitigation: 'FinOps' },
    { risk: 'Security vulnerabilities', impact: 'Critical', probability: 'Low', mitigation: 'Security audits' },
    { risk: 'Integration complexity', impact: 'High', probability: 'Medium', mitigation: 'Phased rollout' },
    { risk: 'Skill shortage', impact: 'Medium', probability: 'Medium', mitigation: 'Training' },
    { risk: 'Compliance violations', impact: 'Critical', probability: 'Low', mitigation: 'Compliance framework' },
    { risk: 'Vendor lock-in', impact: 'Medium', probability: 'Medium', mitigation: 'Open standards' }
  ];
  
  // Header
  pdf.setFillColor(30, 64, 175);
  pdf.rect(margin, y, contentWidth, 8, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Risk', margin + 5, y + 6);
  pdf.text('Impact', margin + 120, y + 6);
  pdf.text('Probability', margin + 160, y + 6);
  pdf.text('Mitigation', margin + 210, y + 6);
  y += 9;
  
  risks.forEach((risk, i) => {
    if (i % 2 === 0) {
      pdf.setFillColor(245, 247, 250);
      pdf.rect(margin, y, contentWidth, 6, 'F');
    }
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.text(risk.risk.substring(0, 40), margin + 5, y + 5);
    pdf.text(risk.impact, margin + 120, y + 5);
    pdf.text(risk.probability, margin + 160, y + 5);
    pdf.text(risk.mitigation.substring(0, 40), margin + 210, y + 5);
    y += 7;
  });
}

// ============================================================
// 15. RECOMMENDATIONS
// ============================================================
function generateRecommendations(pdf, margin, contentWidth, pageHeight) {
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('Expert Recommendations', margin, 25);
  pdf.line(margin, 30, margin + contentWidth, 30);
  
  let y = 42;
  const recommendations = [
    { title: 'Implement Comprehensive Monitoring', desc: 'Full-stack monitoring with Prometheus, Grafana, ELK' },
    { title: 'Establish Data Governance', desc: 'Data quality, lineage, access controls' },
    { title: 'Use MLOps Best Practices', desc: 'CI/CD, model versioning, automated testing' },
    { title: 'Plan for Scalability', desc: 'Horizontal scaling with Kubernetes' },
    { title: 'Invest in Security', desc: 'Defense-in-depth with encryption' },
    { title: 'Document Everything', desc: 'Model cards, data cards, documentation' },
    { title: 'Continuous Improvement', desc: 'Feedback loops for retraining' }
  ];
  
  recommendations.forEach((rec, i) => {
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${i + 1}. ${rec.title}`, margin, y);
    y += 6;
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text(`   ${rec.desc}`, margin, y);
    y += 10;
  });
}

// ============================================================
// HELPER: Get Layers for Approach
// ============================================================
function getLayersForApproach(approachId) {
  const layerMap = {
    1: ['layer1','layer2','layer3','layer4','layer5','layer6','layer7','layer8','layer9','layer10','layer11','layer12'],
    2: ['layer10','layer11'],
    4: ['layer3','layer4','layer5','layer6','layer8','layer10'],
    7: ['layer1','layer3','layer4','layer5','layer6','layer8'],
    8: ['layer1','layer2','layer3','layer4','layer5','layer6','layer7','layer8','layer9','layer10','layer11','layer12'],
    9: ['layer1','layer3','layer4','layer6','layer8','layer10','layer11'],
    12: ['layer1','layer2','layer3','layer5','layer6','layer8','layer10'],
    21: ['layer1','layer6','layer8'],
    22: ['layer1','layer6','layer8'],
    24: ['layer3','layer4','layer6','layer8','layer11','layer12'],
    27: ['layer5','layer6','layer7','layer11'],
    30: ['layer11','layer12'],
    36: ['layer1','layer3','layer4','layer5','layer6','layer8','layer11'],
    37: ['layer2','layer5','layer6','layer8','layer10','layer11'],
    39: ['layer8','layer9','layer11','layer12']
  };
  return layerMap[approachId] || [];
}

// ============================================================
// ADD FOOTERS
// ============================================================
function addFooters(pdf, data, pageWidth, pageHeight) {
  const pageCount = pdf.internal.getNumberOfPages();
  
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.5);
    pdf.line(10, pageHeight - 10, pageWidth - 10, pageHeight - 10);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(150, 150, 150);
    pdf.text(data?.name || 'AI Solution', 10, pageHeight - 4);
    pdf.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 4, { align: 'center' });
    pdf.text(new Date().toLocaleDateString(), pageWidth - 10, pageHeight - 4, { align: 'right' });
  }
}

export default generateEnhancedPDFReport;